import LockIcon from '@mui/icons-material/Lock'
import { Avatar, Button, Card, CardActions, CircularProgress, TextField } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { Notification, useLogin, useNotify, useTranslate } from 'react-admin'
import { Controller, useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { isWrongCode, SecondFactorRequired } from '~/helpers'
import { lightTheme } from './themes'

const useStyles = makeStyles()((theme) => ({
  main: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'flex-start',
    background: 'url(https://images.unsplash.com/photo-1610665899459-0f6857552744)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  card: {
    minWidth: 300,
    marginTop: '6em',
  },
  avatar: {
    margin: '1em',
    display: 'flex',
    justifyContent: 'center',
  },
  icon: {
    backgroundColor: theme.palette.secondary.main,
  },
  hint: {
    marginTop: '1em',
    display: 'flex',
    justifyContent: 'center',
    color: theme.palette.grey[500],
  },
  form: {
    padding: '0 1em 1em 1em',
  },
  input: {
    marginTop: '1em',
  },
  actions: {
    padding: '0 1em 1em 1em',
  },
}))

interface FormValues {
  username?: string
  password?: string
  otp?: string
}

const Login = () => {
  const [loading, setLoading] = useState(false)
  const translate = useTranslate()
  const { classes } = useStyles()
  const notify = useNotify()
  const login = useLogin()
  const location = useLocation()
  // Set once the password was right and the account wants its second factor.
  const [ticket, setTicket] = useState<string | null>(null)

  const handleSubmit = (auth: FormValues) => {
    setLoading(true)
    const params = ticket ? { ticket, otp: auth.otp } : auth
    login(params, location.state ? location.state.nextPathname : '/').catch((error: Error) => {
      setLoading(false)
      if (error instanceof SecondFactorRequired) {
        setTicket(error.ticket)
        return
      }
      if (ticket && isWrongCode(error)) {
        notify('auth.mfa.invalid', { type: 'warning' })
        // nhost/auth spends the ticket on a wrong code: fetch a new one with
        // the password still in the form, so a typo does not mean starting over.
        setValue('otp', '')
        const { username, password } = getValues()
        login({ username, password }).catch((again: Error) =>
          setTicket(again instanceof SecondFactorRequired ? again.ticket : null)
        )
        return
      }
      notify(
        typeof error === 'string'
          ? error
          : typeof error === 'undefined' || !error.message
            ? 'ra.auth.sign_in_error'
            : error.message,
        {
          type: 'warning',
          messageArgs: {
            _:
              typeof error === 'string'
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        }
      )
    })
  }

  // react-hook-form replaces react-final-form's <Form render={...}> and
  // <Field component={...}>. Controller is the equivalent of <Field> for
  // wiring a plain MUI input into the form.
  const {
    control,
    handleSubmit: rhfHandleSubmit,
    getValues,
    setValue,
  } = useForm<FormValues>({
    defaultValues: { username: '', password: '', otp: '' },
  })

  const field = (name: 'username' | 'password' | 'otp', label: string, type?: string) => (
    <div className={classes.input}>
      <Controller
        name={name}
        control={control}
        rules={{ required: translate('ra.validation.required') }}
        render={({ field: inputProps, fieldState }) => (
          <TextField
            {...inputProps}
            type={type}
            label={label}
            disabled={loading}
            error={!!fieldState.error}
            helperText={
              fieldState.error?.message ?? (name === 'otp' ? translate('auth.mfa.hint') : '')
            }
            autoFocus={name === 'username' || name === 'otp'}
            fullWidth
            slotProps={
              name === 'otp'
                ? {
                    htmlInput: {
                      inputMode: 'numeric',
                      autoComplete: 'one-time-code',
                      maxLength: 6,
                    },
                  }
                : undefined
            }
          />
        )}
      />
    </div>
  )

  return (
    <form onSubmit={rhfHandleSubmit(handleSubmit)} noValidate>
      <div className={classes.main}>
        <Card className={classes.card}>
          <div className={classes.avatar}>
            <Avatar className={classes.icon}>
              <LockIcon />
            </Avatar>
          </div>
          <div className={classes.hint}>{ticket ? translate('auth.mfa.title') : 'Login'}</div>
          <div className={classes.form}>
            {ticket ? (
              field('otp', translate('auth.mfa.code'))
            ) : (
              <>
                {field('username', translate('ra.auth.username'))}
                {field('password', translate('ra.auth.password'), 'password')}
              </>
            )}
          </div>
          <CardActions className={classes.actions} sx={{ flexDirection: 'column', gap: 1 }}>
            <Button variant='contained' type='submit' color='primary' disabled={loading} fullWidth>
              {loading && <CircularProgress size={25} thickness={2} />}
              {translate(ticket ? 'auth.mfa.confirm' : 'ra.auth.sign_in')}
            </Button>
            {ticket && (
              <Button onClick={() => setTicket(null)} disabled={loading} fullWidth>
                {translate('auth.mfa.back')}
              </Button>
            )}
          </CardActions>
        </Card>
        <Notification />
      </div>
    </form>
  )
}

Login.propTypes = {
  authProvider: PropTypes.func,
  previousRoute: PropTypes.string,
}

// We need to put the ThemeProvider decoration in another component
// Because otherwise the useStyles() hook used in Login won't get
// the right theme
const LoginWithTheme = (props: any) => (
  <ThemeProvider theme={createTheme(lightTheme)}>
    <Login {...props} />
  </ThemeProvider>
)

export default LoginWithTheme
